import { Check, ChevronDown } from 'lucide-react'
import { Link, NavLink, useLocation } from 'react-router'
import useSWR from 'swr'

import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { apiFetcher } from '@/data/fetcher'
import { EetStatus } from '@invo.space/shared'
import { cn } from '@/lib/utils'

const items = [
  { to: '/cash-register', label: 'Pokladna', end: true },
  { to: '/cash-register/receipts', label: 'Účtenky' },
  { to: '/cash-register/dashboard', label: 'Přehledy' },
  { to: '/cash-register/eet', label: 'EET 2.0', eet: true },
  { to: '/cash-register/settings', label: 'Nastavení' },
]

/** Navigace mezi obrazovkami pokladny bez rámečků: neaktivní jen tlumený text, aktivní jemná pilulka. */
export const CashRegisterNav = ({ touch = false, className }: { touch?: boolean; className?: string }) => {
  const { data: eet } = useSWR<EetStatus>('/api/v2/eet', apiFetcher)

  return (
    <nav aria-label="Pokladna" className={cn('inline-flex flex-wrap items-center gap-1', className)}>
      {items
        .filter((item) => !item.eet || eet?.isCzech)
        .map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'inline-flex items-center rounded-full font-medium transition-colors',
                'focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none',
                touch ? 'h-11 px-4 text-sm' : 'h-7 px-3 text-[13px]',
                isActive ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
              )
            }
          >
            {item.label}
          </NavLink>
        ))}
    </nav>
  )
}

/** Na mobilu místo řady pilulek jedno tlačítko s názvem obrazovky a nabídkou ostatních. */
export const CashRegisterNavMenu = ({ className }: { className?: string }) => {
  const { data: eet } = useSWR<EetStatus>('/api/v2/eet', apiFetcher)
  const { pathname } = useLocation()
  const visible = items.filter((item) => !item.eet || eet?.isCzech)
  const current = visible.find((i) => (i.end ? pathname === i.to : pathname.startsWith(i.to))) ?? visible[0]

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className={cn('h-10 gap-1.5 rounded-full px-3 text-base font-medium', className)}>
          {current.label}
          <ChevronDown className="size-4 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        {visible.map((item) => (
          <DropdownMenuItem key={item.to} asChild className="h-11">
            <Link to={item.to}>
              <span className="flex-1">{item.label}</span>
              {item === current && <Check className="size-4" />}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
